import { Injectable, signal, computed, inject, PLATFORM_ID, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { filter } from 'rxjs/operators';
import { ADMIN_ROUTES } from '../../constants/routes.constants';
import { AuthService } from '../auth/auth.service';
import { MOBILE_BREAKPOINT } from 'src/app/shared/constants/breakpoints';

@Injectable({
  providedIn: 'root'
})
export class SidebarService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly authService = inject(AuthService);

  // Estado Interno
  private readonly _isOpen = signal(false);
  private readonly _windowWidth = signal(0);
  private readonly _currentUrl = signal('');

  // Seletores Públicos
  public readonly isOpen = computed(() => this._isOpen());
  public readonly isMobile = computed(() => this._windowWidth() > 0 && this._windowWidth() <= MOBILE_BREAKPOINT);
  
  // Modo Push: Apenas Desktop e Rotas Administrativas
  public readonly isPushMode = computed(() => {
    // Se for mobile, nunca é push (sempre overlay)
    if (this.isMobile()) return false;
    
    // Se não estiver no browser (SSR), assume false por segurança
    if (!isPlatformBrowser(this.platformId)) return false;

    // Verifica se é rota administrativa
    const url = this._currentUrl();

    if (url === '/eventos' || url.startsWith('/eventos?')) {
      return !this.authService.isLogged() || this.authService.isProducer() || this.authService.isAdmin();
    }

    // Dentro de /eventos só a área de gestão é push; detalhe público e demais
    // subrotas ficam sem menu.
    if (url.startsWith('/eventos/')) {
      const isEventsAdmin =
        url.startsWith('/eventos/registrar') ||
        url.startsWith('/eventos/editar') ||
        url.startsWith('/eventos/configuracao') ||
        url.startsWith('/eventos/meus-eventos');
      if (!isEventsAdmin) return false;
    }

    return ADMIN_ROUTES.some((route) => url.startsWith(route));
  });

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      // Inicialização
      this._windowWidth.set(window.innerWidth);
      this._currentUrl.set(this.router.url);

      // Listener de Resize
      // Usando arrow function para manter o 'this' e facilitar remoção se necessário (embora seja singleton)
      window.addEventListener('resize', this.onResize);

      // Monitoramento de Rotas
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      ).subscribe((event) => {
        const url = event.urlAfterRedirects || event.url;
        this._currentUrl.set(url);
        this.checkAutoOpenState();
      });
      
      // Verificação inicial
      this.checkAutoOpenState();
    }
  }

  private onResize = () => {
    this._windowWidth.set(window.innerWidth);
  }

  // Push abre o menu automaticamente; qualquer outra rota o mantém fechado.
  private checkAutoOpenState() {
    if (!this.isPushMode()) {
      this.close();
      return;
    }
    setTimeout(() => {
      if (this.isPushMode() && !this._isOpen()) this.open();
    }, 0);
  }

  toggle(): void {
    this._isOpen.update(v => !v);
  }

  open(): void {
    this._isOpen.set(true);
  }

  close(): void {
    this._isOpen.set(false);
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.onResize);
    }
  }
}
